import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../shared/store/store';
import { hsnCodeService } from '../services/hsnCodeService';
import { HsnCodeState } from '../types/hsnCode.types';

const tok = (s: RootState) => s.auth.accessToken ?? '';

export const fetchHsnCodes = createAsyncThunk(
    'sellerHsn/fetchAll',
    async (params: Record<string, any>, { getState }) => {
        const { data } = await hsnCodeService.getHsnCodes(params, tok(getState() as RootState));
        return data;
    }
);

export const createHsnCode = createAsyncThunk(
    'sellerHsn/create',
    async (payload: { hsnCode: string; description: string; gstRate: number }, { getState }) => {
        const { data } = await hsnCodeService.createHsnCode(payload, tok(getState() as RootState));
        return data.hsnCode;
    }
);

export const updateHsnCode = createAsyncThunk(
    'sellerHsn/update',
    async ({ id, body }: { id: string; body: Partial<{ description: string; gstRate: number; status: string }> }, { getState }) => {
        const { data } = await hsnCodeService.updateHsnCode(id, body, tok(getState() as RootState));
        return data.hsnCode;
    }
);

export const deleteHsnCode = createAsyncThunk(
    'sellerHsn/delete',
    async (id: string, { getState }) => {
        await hsnCodeService.deleteHsnCode(id, tok(getState() as RootState));
        return id;
    }
);

export const toggleHsnStatus = createAsyncThunk(
    'sellerHsn/toggleStatus',
    async ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' }, { getState }) => {
        await hsnCodeService.toggleStatus(id, status, tok(getState() as RootState));
        return { id, status };
    }
);

const initialState: HsnCodeState = {
    hsnCodes: [],
    total   : 0,
    page    : 1,
    loading : false,
    error   : null,
};

const hsnCodeSlice = createSlice({
    name: 'sellerHsn',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchHsnCodes.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(fetchHsnCodes.fulfilled, (s, a) => {
                s.loading  = false;
                s.hsnCodes = a.payload.hsnCodes;
                s.total    = a.payload.total;
                s.page     = a.payload.page;
            })
            .addCase(fetchHsnCodes.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(createHsnCode.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(createHsnCode.fulfilled, (s, a) => { s.loading = false; s.hsnCodes.unshift(a.payload); s.total += 1; })
            .addCase(createHsnCode.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(updateHsnCode.fulfilled, (s, a) => {
                if (!a.payload) return;
                const i = s.hsnCodes.findIndex(h => h._id === a.payload._id);
                if (i !== -1) s.hsnCodes[i] = a.payload;
            });

        builder
            .addCase(deleteHsnCode.fulfilled, (s, a) => {
                s.hsnCodes = s.hsnCodes.filter(h => h._id !== a.payload);
                s.total -= 1;
            });

        builder
            .addCase(toggleHsnStatus.fulfilled, (s, a) => {
                const h = s.hsnCodes.find(h => h._id === a.payload.id);
                if (h) h.status = a.payload.status;
            });
    },
});

export default hsnCodeSlice.reducer;
